import { Button } from '@/components/ui/button';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ScoreInputProps {
  playerName: string;
  score: number | null;
  par: number;
  receivesStroke: boolean;
  onChange: (score: number | null) => void;
  teamColor?: 'team1' | 'team2';
}

function getScoreLabel(score: number, par: number): string {
  const diff = score - par;
  if (diff <= -2) return 'Eagle';
  if (diff === -1) return 'Birdie';
  if (diff === 0) return 'Par';
  if (diff === 1) return 'Bogey';
  if (diff === 2) return 'Dbl Bogey';
  return `+${diff}`;
}

function getScoreColor(score: number | null, par: number): string {
  if (score === null) return '';
  const diff = score - par;
  if (diff <= -2) return 'text-yellow-600 font-bold';
  if (diff === -1) return 'text-green-600 font-bold';
  if (diff === 0) return 'text-gray-800';
  if (diff === 1) return 'text-red-500';
  return 'text-red-700 font-bold';
}

export function ScoreInput({ playerName, score, par, receivesStroke, onChange, teamColor = 'team1' }: ScoreInputProps) {
  const bgClass = teamColor === 'team1' ? 'bg-primary/10 border-primary/30' : 'bg-[#9966CC]/10 border-[#9966CC]/30';

  return (
    <div className={cn('rounded-lg border p-3', bgClass)} data-testid={`score-input-row-${playerName}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">{playerName}</span>
          {receivesStroke && (
            <span className="w-2.5 h-2.5 bg-black rounded-full" title="Receives stroke" />
          )}
        </div>
        {score !== null && (
          <span className={cn('text-xs', getScoreColor(score, par))}>
            {getScoreLabel(score, par)}
          </span>
        )}
      </div>
      <div className="flex items-center justify-center gap-3">
        <Button
          variant="outline"
          className="min-w-[44px] min-h-[44px] rounded-full text-lg font-bold"
          onClick={() => {
            if (score === null) {
              onChange(par > 1 ? par - 1 : 1);
            } else if (score > 1) {
              onChange(score - 1);
            }
          }}
          data-testid={`btn-minus-${playerName}`}
        >
          <Minus className="w-5 h-5" />
        </Button>
        <div
          className={cn(
            'min-w-[56px] min-h-[56px] flex items-center justify-center rounded-xl border-2 text-2xl font-bold',
            score !== null ? 'border-gray-300 bg-white' : 'border-dashed border-gray-300 bg-gray-50',
            getScoreColor(score, par)
          )}
          data-testid={`score-display-${playerName}`}
        >
          {score !== null ? score : '-'}
        </div>
        <Button
          variant="outline"
          className="min-w-[44px] min-h-[44px] rounded-full text-lg font-bold"
          onClick={() => {
            if (score === null) {
              onChange(par + 1);
            } else if (score < 15) {
              onChange(score + 1);
            }
          }}
          data-testid={`btn-plus-${playerName}`}
        >
          <Plus className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}
